import { FC, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AnySchema, date, object, string } from "yup";
import { DateField } from "../../../../../components/forms/date";
import { TextField } from "../../../../../components/forms/text";
import { GridItem } from "../../../../../components/layout";

const useSchema = () => {
  const { t } = useTranslation("fields");

  return useMemo(
    () =>
      object()
        .shape({
          number: string()
            .label(t("identity-document-number"))
            .required(
              t("required-field", {
                ns: "errors",
                field: t("identity-document-number"),
              })
            ),
          issueDate: date()
            .max(new Date(), t("issue-date-cannot-be-in-the-future"))
            .label(t("identity-document-issue-date"))
            .required(
              t("required-field", {
                ns: "errors",
                field: t("identity-document-issue-date"),
              })
            ),
          expiryDate: date()
            .min(new Date(), t("expiration-date-cannot-be-in-the-past"))
            .label(t("identity-document-expiry-date"))
            .required(
              t("required-field", {
                ns: "errors",
                field: t("identity-document-issue-date"),
              })
            ),
        })
        .required(
          t("required-field", {
            ns: "errors",
            field: t("identity-document"),
          })
        )
        .label(t("identity-document")),
    [t]
  );
};

const Elem: FC<{ path: string }> = ({ path }) => {
  const { t } = useTranslation("fields");
  const { control } = useFormContext();

  return (
    <>
      <GridItem top xs={12} md={4}>
        <Controller
          control={control}
          name={`${path}.number`}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              error={!!error}
              helperText={error?.message}
              fullWidth
              label={t("document-number")}
            />
          )}
        />
      </GridItem>
      <GridItem top xs={12} sm={6} md={4}>
        <Controller
          control={control}
          name={`${path}.issueDate`}
          render={({ field, fieldState: { error } }) => (
            <DateField
              error={!!error}
              helperText={error?.message}
              {...field}
              fullWidth
              label={t("issue-date")}
            />
          )}
        />
      </GridItem>
      <GridItem top xs={12} sm={6} md={4}>
        <Controller
          control={control}
          name={`${path}.expiryDate`}
          render={({ field, fieldState: { error } }) => (
            <DateField
              error={!!error}
              helperText={error?.message}
              {...field}
              fullWidth
              label={t("expiry-date")}
            />
          )}
        />
      </GridItem>
    </>
  );
};

export const useIdentityDocumentDetails = () => {
  const schema: AnySchema = useSchema();
  return useMemo(
    () => ({
      schema,
      elem: Elem,
    }),
    [schema]
  );
};
